// src/hooks/useXpBonus.js
import { useEffect, useMemo, useState } from 'react';
import { useGamification } from '../hooks/GamificationContext';

export const XP_EVENTS = {
  ADD_PFAND: 'add_pfand',
  MARK_PFANDSPENDE: 'mark_pfandspende',
  PICKUP_PFANDSPENDE: 'pickup_pfandspende',
  PFANDSPENDE_CONFIRMED: 'pfandspende_confirmed',
  REMOVE_ENTRY: 'remove_entry',
  BARCODE_SCAN: 'barcode_scan',
  FREITEXT: 'freitext',
  JOIN_PROJECT: 'join_project',
  COMPLETE_DAILY: 'complete_daily',
  STEPS_GOAL: 'steps_goal',
  STEPS_STREAK_3: 'steps_streak_3',
  STEPS_STREAK_7: 'steps_streak_7',
  STEPS_STREAK_30: 'steps_streak_30',
  STEPS_TOTAL_10000: 'steps_total_10000',
  STEPS_TOTAL_50000: 'steps_total_50000',
  STEPS_TOTAL_100000: 'steps_total_100000',
  LOOT_FIND: 'loot_find',
  REDEEM_PFAND: 'redeem_pfand',
  USE_XP_BOOST: 'use_xp_boost',
  OPEN_BOTTLE_MESSAGE: 'open_bottle_message',
  EXPEDITION_SCAN: 'expedition_scan',
  EXPEDITION_PFAND_BONUS: 'expedition_pfand_bonus',
  EXPEDITION_MAP_COMPLETE: 'expedition_map_complete',
  REFERRAL_QUALIFIED: 'referral_qualified',
  DRINK_SUBMISSION_APPROVED: 'drink_submission_approved',
  TAVERN_GAME_COMPLETE: 'tavern_game_complete',
};

const defaultXpConfig = {
  [XP_EVENTS.ADD_PFAND]: 1,
  [XP_EVENTS.MARK_PFANDSPENDE]: 5,
  [XP_EVENTS.PICKUP_PFANDSPENDE]: 8,
  [XP_EVENTS.PFANDSPENDE_CONFIRMED]: 10,
  [XP_EVENTS.REMOVE_ENTRY]: -1,
  [XP_EVENTS.BARCODE_SCAN]: 0,
  [XP_EVENTS.FREITEXT]: 1,
  [XP_EVENTS.JOIN_PROJECT]: 0,
  [XP_EVENTS.COMPLETE_DAILY]: 100,
  [XP_EVENTS.STEPS_GOAL]: 100,
  [XP_EVENTS.STEPS_STREAK_3]: 200,
  [XP_EVENTS.STEPS_STREAK_7]: 500,
  [XP_EVENTS.STEPS_STREAK_30]: 2000,
  [XP_EVENTS.STEPS_TOTAL_10000]: 3000,
  [XP_EVENTS.STEPS_TOTAL_50000]: 1000,
  [XP_EVENTS.STEPS_TOTAL_100000]: 10000,
  [XP_EVENTS.LOOT_FIND]: 2,
  [XP_EVENTS.REDEEM_PFAND]: 5,
  [XP_EVENTS.USE_XP_BOOST]: 100,
  [XP_EVENTS.OPEN_BOTTLE_MESSAGE]: 1,
  [XP_EVENTS.EXPEDITION_SCAN]: 10,
  [XP_EVENTS.EXPEDITION_PFAND_BONUS]: 18,
  [XP_EVENTS.EXPEDITION_MAP_COMPLETE]: 150,
  [XP_EVENTS.REFERRAL_QUALIFIED]: 100,
  [XP_EVENTS.DRINK_SUBMISSION_APPROVED]: 5,
  [XP_EVENTS.TAVERN_GAME_COMPLETE]: 20,
};

export const XP_EVENT_META = {
  [XP_EVENTS.MARK_PFANDSPENDE]: { icon: 'XP', label: 'Fund markiert' },
  [XP_EVENTS.PICKUP_PFANDSPENDE]: { icon: 'XP', label: 'Pfandspende abgeholt' },
  [XP_EVENTS.PFANDSPENDE_CONFIRMED]: { icon: 'XP', label: 'Meldung hat geholfen' },
  [XP_EVENTS.ADD_PFAND]:       { icon: '🧃', label: 'Pfand hinzugefügt' },
  [XP_EVENTS.REMOVE_ENTRY]:    { icon: '🗑️', label: 'Pfandeintrag gelöscht' },
  [XP_EVENTS.BARCODE_SCAN]:    { icon: '📷', label: 'Barcode gescannt' },
  [XP_EVENTS.FREITEXT]:        { icon: '⌨️', label: 'Pfand manuell hinzugefügt' },
  [XP_EVENTS.JOIN_PROJECT]:    { icon: '🤝', label: 'Projekt beigetreten' },
  [XP_EVENTS.COMPLETE_DAILY]:  { icon: '🎯', label: 'Tagesaufgabe erfüllt' },
  [XP_EVENTS.STEPS_GOAL]:      { icon: '👣', label: '5000-Schritte-Ziel erreicht' },
  [XP_EVENTS.STEPS_STREAK_3]:  { icon: '🔥', label: 'Schritte-Streak 3 Tage' },
  [XP_EVENTS.STEPS_STREAK_7]:  { icon: '🔥', label: 'Schritte-Streak 7 Tage' },
  [XP_EVENTS.STEPS_STREAK_30]: { icon: '🔥', label: 'Schritte-Streak 30 Tage' },
  [XP_EVENTS.STEPS_TOTAL_10000]:  { icon: '🥾', label: '10.000 Schritte gesamt' },
  [XP_EVENTS.STEPS_TOTAL_50000]:  { icon: '🥾', label: '50.000 Schritte gesamt' },
  [XP_EVENTS.STEPS_TOTAL_100000]: { icon: '🥾', label: '100.000 Schritte gesamt' },
  [XP_EVENTS.LOOT_FIND]:       { icon: '🪙', label: 'Loot gefunden' },
  [XP_EVENTS.REDEEM_PFAND]:    { icon: '💰', label: 'Pfand eingelöst' },
  [XP_EVENTS.USE_XP_BOOST]:    { icon: '⚡', label: 'XP-Boost benutzt' },
  [XP_EVENTS.OPEN_BOTTLE_MESSAGE]: { icon: '🍾', label: 'Flaschenpost geöffnet' },
  [XP_EVENTS.EXPEDITION_SCAN]: { icon: '🧭', label: 'Expedition abgeschlossen' },
  [XP_EVENTS.EXPEDITION_PFAND_BONUS]: { icon: '🎣', label: 'Pfand-Beifang entdeckt' },
  [XP_EVENTS.EXPEDITION_MAP_COMPLETE]: { icon: '🗺️', label: 'Schatzkarte vervollständigt' },
  [XP_EVENTS.REFERRAL_QUALIFIED]: { icon: '🤝', label: 'Freund erfolgreich eingeladen' },
  [XP_EVENTS.DRINK_SUBMISSION_APPROVED]: { icon: '🥤', label: 'Getränkevorschlag freigegeben' },
  [XP_EVENTS.TAVERN_GAME_COMPLETE]: { icon: '🍺', label: 'Tavernenrunde abgeschlossen' },
  generic_gain: { icon: '🏅', label: 'XP erhalten' },
  generic_loss: { icon: '⚠️', label: 'XP verloren' },
};

export function getXpToastMeta(eventKey, awarded = 0, explicitLabel = '') {
  if (explicitLabel) {
    return {
      icon: awarded < 0 ? '⚠️' : '🏅',
      label: explicitLabel,
    };
  }

  if (eventKey && XP_EVENT_META[eventKey]) {
    return XP_EVENT_META[eventKey];
  }

  return awarded < 0 ? XP_EVENT_META.generic_loss : XP_EVENT_META.generic_gain;
}

export function useXpBonus() {
  const { addXP } = useGamification();
  const [xpConfig, setXpConfig] = useState(defaultXpConfig);

  useEffect(() => {
    let active = true;
    const url = window?.pfandpiratData?.xpConfigApiUrl || '/wp-json/pfandpirat/v1/xp-config';

    (async () => {
      try {
        const res = await fetch(url, {
          credentials: 'include',
          headers: {
            'X-WP-Nonce': window?.pfandpiratData?.nonce || '',
          },
        });

        if (!res.ok) return;

        const data = await res.json();
        if (active && data && typeof data === 'object') {
          setXpConfig((prev) => ({ ...prev, ...data }));
        }
      } catch (e) {
        console.warn('XP config load failed', e);
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  const resolveXp = useMemo(() => {
    return (eventKey, fallbackPoints = null) => {
      if (eventKey && Object.prototype.hasOwnProperty.call(xpConfig, eventKey)) {
        return Number(xpConfig[eventKey] ?? 0);
      }
      if (fallbackPoints !== null && fallbackPoints !== undefined) {
        return Number(fallbackPoints || 0);
      }
      return 0;
    };
  }, [xpConfig]);

  const giveXP = async (eventKey, fallbackPoints = null, explicitLabel = '', options = {}) => {
    const optimistic = resolveXp(eventKey, fallbackPoints);
    const result = await addXP({
      event: eventKey,
      points: optimistic,
      optimistic,
      label: explicitLabel,
      emitXpToast: options.emitXpToast !== false,
    });

    if (result && typeof result.awarded === 'number') {
      return result.awarded;
    }

    return optimistic;
  };

  return { giveXP, xpConfig, resolveXp };
}
